import { useEffect, useRef } from "react";
import { useLocation } from "react-router";
import {
  useCookieConsent,
  CONSENT_VERSION,
  CONSENT_STORAGE_KEY,
} from "@/hooks/use-cookie-consent";
import { useAuth } from "@/lib/local-auth";
import { analyticsPath } from "../../../shared/network-analytics";

declare global {
  interface Window {
    EnkeyAnalytics?: {
      start: (options: {
        siteId: string;
        endpoint: string;
        consentVersion: string;
        consent: () => boolean;
        identityConsent: () => boolean;
      }) => (clear?: boolean) => void;
      stop: (clear?: boolean) => void;
      event: (type: "goal" | "outbound" | "download", target: string) => void;
    };
  }
}
export const IDENTITY_CONSENT_KEY = "enkey.analytics.account-consent.v1";
export default function NetworkAnalytics() {
  const { consent } = useCookieConsent();
  const { pathname } = useLocation();
  const { isAuthenticated } = useAuth();
  const allowed = !!consent?.analytics && !!analyticsPath(pathname);
  const consentRef = useRef(!!consent?.analytics);
  consentRef.current = !!consent?.analytics;
  const accountRef = useRef(isAuthenticated);
  accountRef.current = isAuthenticated;
  useEffect(() => {
    if (!allowed) {
      window.EnkeyAnalytics?.stop(!consent?.analytics);
      if (!consent?.analytics) {
        try {
          localStorage.removeItem("enkey.analytics.visitor.v1");
          localStorage.removeItem(IDENTITY_CONSENT_KEY);
        } catch {
          /* no storage */
        }
      }
      return;
    }
    const controller = new AbortController();
    let stop: (clear?: boolean) => void = () => {};
    let cancelled = false;
    const hasConsent = () => {
      if (!consentRef.current) return false;
      try {
        const stored = JSON.parse(
          localStorage.getItem(CONSENT_STORAGE_KEY) || "null",
        );
        return stored?.version === CONSENT_VERSION && stored.analytics === true;
      } catch {
        return false;
      }
    };
    void (async () => {
      const response = await fetch(
        "/api/network-analytics/config?site=enkey-main",
        { cache: "no-store", signal: controller.signal },
      );
      if (!response.ok) return;
      const config = await response.json();
      if (cancelled || !config.enabled || !hasConsent()) return;
      if (!window.EnkeyAnalytics)
        await new Promise<void>((resolve, reject) => {
          const script = document.createElement("script");
          script.src = "/enkey-analytics.js";
          script.async = true;
          script.onload = () => resolve();
          script.onerror = () => reject(new Error("TRACKER_UNAVAILABLE"));
          document.head.appendChild(script);
        });
      if (cancelled || !hasConsent()) return;
      stop = window.EnkeyAnalytics!.start({
        siteId: config.siteId,
        endpoint: "/api/network-analytics",
        consentVersion: String(CONSENT_VERSION),
        consent: hasConsent,
        identityConsent: () => {
          try {
            return (
              config.identityEnabled &&
              accountRef.current &&
              localStorage.getItem(IDENTITY_CONSENT_KEY) === "true"
            );
          } catch {
            return false;
          }
        },
      });
    })().catch(() => {
      /* Analytics must never break navigation or log visitor data. */
    });
    return () => {
      cancelled = true;
      controller.abort();
      stop(!consentRef.current);
    };
  }, [allowed, consent?.analytics]);
  return null;
}
